import { useState, useEffect } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import { McpAiChat, AiSettings } from '@/ai';
import { useIsMobile } from '@/hooks';
import { SkipNav } from './SkipNav';
import { MobileNavigation } from './MobileNavigation';
import { MobileBottomNav } from './MobileBottomNav';
import { ThemeToggleButton } from './ui/ThemeToggle';
import { useFocusTrap } from '@/hooks';
import { cn } from '@/lib/utils';
import { FileToolbar, DropZone, SettingsDialog } from '@/storage';
import { DocsPanel, useDocsStore } from '@/docs';
import { OnboardingProvider } from '@/onboarding';

// Navigation icons as React components
function HomeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  );
}

function CodeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="16 18 22 12 16 6" />
      <polyline points="8 6 2 12 8 18" />
    </svg>
  );
}

function WindIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.7 7.7a2.5 2.5 0 1 1 1.8 4.3H2" />
      <path d="M9.6 4.6A2 2 0 1 1 11 8H2" />
      <path d="M12.6 19.4A2 2 0 1 0 14 16H2" />
    </svg>
  );
}

function PlantIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 20a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8l-7 5V8l-7 5V4a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z" />
      <path d="M17 18h1" />
      <path d="M12 18h1" />
      <path d="M7 18h1" />
    </svg>
  );
}

function CalendarIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}

function TemplateIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
      <line x1="3" y1="9" x2="21" y2="9" />
      <line x1="9" y1="21" x2="9" y2="9" />
    </svg>
  );
}

function CubeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
      <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
      <line x1="12" y1="22.08" x2="12" y2="12" />
    </svg>
  );
}

function CloudSunIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2v2" />
      <path d="m4.93 4.93 1.41 1.41" />
      <path d="M20 12h2" />
      <path d="m19.07 4.93-1.41 1.41" />
      <path d="M15.947 12.65a4 4 0 0 0-5.925-4.128" />
      <path d="M13 22H7a5 5 0 1 1 4.9-6H13a3 3 0 0 1 0 6Z" />
    </svg>
  );
}

function PlayIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="5 3 19 12 5 21 5 3" />
    </svg>
  );
}

function BarChartIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="20" x2="12" y2="10" />
      <line x1="18" y1="20" x2="18" y2="4" />
      <line x1="6" y1="20" x2="6" y2="16" />
    </svg>
  );
}

function BookOpenIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
      <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
    </svg>
  );
}

// Navigation links configuration
const navigationLinks = [
  { href: '/', label: 'Home', icon: HomeIcon },
  { href: '/editor', label: 'Editor', icon: CodeIcon },
  { href: '/hvac', label: 'Air Loops', icon: WindIcon },
  { href: '/plant', label: 'Plant', icon: PlantIcon },
  { href: '/schedules', label: 'Schedules', icon: CalendarIcon },
  { href: '/templates', label: 'Templates', icon: TemplateIcon },
  { href: '/geometry', label: '3D View', icon: CubeIcon },
  { href: '/weather', label: 'Weather', icon: CloudSunIcon },
  { href: '/docs', label: 'Docs', icon: BookOpenIcon },
  { href: '/simulate', label: 'Simulate', icon: PlayIcon },
  { href: '/results', label: 'Results', icon: BarChartIcon },
];

function DesktopNavigation({
  onOpenAiSettings,
  onOpenSettings,
}: {
  onOpenAiSettings: () => void;
  onOpenSettings: () => void;
}) {
  const location = useLocation();

  return (
    <header className="border-b bg-background sticky top-0 z-40">
      <div className="container flex h-14 items-center px-2 lg:px-4">
        <Link
          to="/"
          className={cn(
            'flex items-center gap-2 font-semibold shrink-0',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-md'
          )}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-6 w-6"
            aria-hidden="true"
          >
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
            <polyline points="9 22 9 12 15 12 15 22" />
          </svg>
          <span className="hidden lg:inline">Envelop</span>
        </Link>

        {/* File Toolbar - Available on all pages */}
        <div className="ml-4 flex-1 max-w-xs md:max-w-sm lg:max-w-md" data-onboarding="file-toolbar">
          <FileToolbar />
        </div>

        <nav
          className="flex items-center gap-0.5"
          role="navigation"
          aria-label="Main navigation"
        >
          {navigationLinks.map(({ href, label, icon: Icon }) => {
            const isActive = location.pathname === href;
            return (
              <Link
                key={href}
                to={href}
                aria-current={isActive ? 'page' : undefined}
                title={label}
                className={cn(
                  'group font-medium transition-all duration-200 ease-out p-1.5 rounded-md flex items-center gap-1 overflow-hidden',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  isActive
                    ? 'text-foreground bg-muted'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                )}
              >
                <Icon className="h-4 w-4 shrink-0" />
                <span
                  className={cn(
                    'text-sm whitespace-nowrap transition-all duration-200 ease-out',
                    'max-w-0 opacity-0 group-hover:max-w-24 group-hover:opacity-100',
                    'xl:max-w-24 xl:opacity-100'
                  )}
                >
                  {label}
                </span>
              </Link>
            );
          })}

          <div className="flex items-center gap-1 ml-1 lg:ml-2 pl-1 lg:pl-2 border-l shrink-0">
            <button
              onClick={onOpenAiSettings}
              className={cn(
                'text-xs lg:text-sm font-medium transition-colors flex items-center gap-1 px-1.5 lg:px-2 py-1 rounded-md',
                'text-muted-foreground hover:text-foreground hover:bg-muted/50',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
              )}
              title="AI Settings"
              aria-label="Open AI settings"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="h-4 w-4"
                aria-hidden="true"
              >
                <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
              <span className="hidden xl:inline">AI</span>
            </button>

            <button
              onClick={onOpenSettings}
              className={cn(
                'text-xs lg:text-sm font-medium transition-colors flex items-center gap-1 px-1.5 lg:px-2 py-1 rounded-md',
                'text-muted-foreground hover:text-foreground hover:bg-muted/50',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
              )}
              title="Settings"
              aria-label="Open settings"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="h-4 w-4"
                aria-hidden="true"
              >
                <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
            </button>

            <span data-onboarding="theme-toggle">
              <ThemeToggleButton />
            </span>
          </div>
        </nav>
      </div>
    </header>
  );
}

function AiSettingsModal({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const modalRef = useFocusTrap<HTMLDivElement>(isOpen);

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ai-settings-title"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal content */}
      <div
        ref={modalRef}
        className="relative w-full max-w-md mx-4"
      >
        <div className="sr-only" id="ai-settings-title">
          AI Settings
        </div>
        <AiSettings onClose={onClose} />
      </div>
    </div>
  );
}

export function Layout() {
  const [showAiSettings, setShowAiSettings] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const isMobile = useIsMobile();

  // Docs panel state - use useShallow to avoid infinite re-renders
  const { isPanelOpen, currentObjectType, currentFieldName, closePanel } = useDocsStore(
    useShallow((state) => ({
      isPanelOpen: state.isPanelOpen,
      currentObjectType: state.currentObjectType,
      currentFieldName: state.currentFieldName,
      closePanel: state.closePanel,
    }))
  );

  return (
    <div className={cn(
      'flex h-app flex-col overflow-hidden',
      // Add bottom padding on mobile to account for bottom nav
      isMobile && 'pb-[60px]'
    )}>
      {/* Skip navigation for keyboard/screen reader users */}
      <SkipNav />

      {/* Responsive navigation */}
      {isMobile ? (
        <MobileNavigation onOpenAiSettings={() => setShowAiSettings(true)} />
      ) : (
        <DesktopNavigation
          onOpenAiSettings={() => setShowAiSettings(true)}
          onOpenSettings={() => setShowSettings(true)}
        />
      )}

      {/* Main content area */}
      <DropZone className="flex-1 overflow-auto">
        <main
          id="main-content"
          className="h-full"
          tabIndex={-1}
          role="main"
        >
          <Outlet />
        </main>
      </DropZone>

      {/* Mobile bottom navigation */}
      {isMobile && <MobileBottomNav />}

      {/* AI Chat Floating Button - MCP-enabled for model interactions */}
      <McpAiChat />

      {/* Documentation Panel - Slide-out panel for object/field docs */}
      {isPanelOpen && (
        <DocsPanel
          isOpen={isPanelOpen}
          objectType={currentObjectType || undefined}
          fieldName={currentFieldName || undefined}
          onClose={closePanel}
        />
      )}

      {/* AI Settings Modal */}
      <AiSettingsModal
        isOpen={showAiSettings}
        onClose={() => setShowAiSettings(false)}
      />

      {/* Settings Dialog */}
      <SettingsDialog
        open={showSettings}
        onOpenChange={setShowSettings}
      />

      {/* Onboarding System */}
      <OnboardingProvider />
    </div>
  );
}
